import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { CompOffService } from '../../../services/compOffService';
import { LeavePolicyService } from '../../../services/leavePolicyService';
import { loadAllUsers, loadDirectReports, SimpleUser } from '../../../services/userLoader';
import { isResolvedIdentity, resolveActorId, sameIdentity } from '../../../services/identity';
import { CompOffRecord } from '../../../types/leavePolicy';
import { LeavePageHeader, YearSelect, isHrRole, isManagerRole } from './leaveShared';
import EmptyState from '../../common/EmptyState';
import { daysLeftLabel, daysUntilExpiry, formatExpiryDate } from '../leaveExpiry';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

/**
 * HISTORY is the manager's read-only view of their team's Comp-Off.
 *
 * It lives here rather than on its own route because this page already loads everything it needs —
 * the direct-reports list, the directory for names, the Comp-Off records and the year — so a
 * separate page meant a second copy of all of it, and a manager checking a claim in Requests had to
 * navigate away to see what the same employee already held.
 */
type Tab = 'MINE' | 'REQUESTS' | 'HISTORY' | 'MANAGE';

const today = () => new Date().toISOString().slice(0, 10);
const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * When a Comp-Off day stops being usable.
 *
 * Comp-Off is earned against a leave year and does not carry into the next one — the balance
 * is computed per year (see CompOffService.getTotalEarned), so a day earned in 2026 counts for
 * 2026 and is gone on 1 January. The record already stored `expiryDate`, but nothing read it
 * and nothing showed it, so an employee had no way to answer "how long do I have to use this?".
 * Falls back to the year end of the worked date when the stored value is missing or malformed,
 * which is what the balance maths does anyway.
 */
const expiryOf = (r: CompOffRecord): string => CompOffService.expiryDateFor(r.workedDate);

/*
 * Date formatting, the countdown and the "days left" wording all live in ../leaveExpiry now.
 * They started here, but every other screen showing a Comp-Off balance needs the same three,
 * and a second copy is how the app ended up stating this one fact three different ways.
 */
const fmtDate = formatExpiryDate;
const daysUntil = daysUntilExpiry;

/**
 * Apply Comp-Off.
 *
 * The employee claims the day they worked; their manager (or HR) decides. Balance
 * moves only on approval — an application on its own credits nothing, so nobody can
 * top up their own balance.
 *
 * HR keeps a direct grant/edit/remove tab for corrections and for crediting someone
 * who never filed a claim.
 */
const ApplyCompOffPage: React.FC = () => {
  const { user } = useAuth();
  const canDecide = isManagerRole(user);
  const isHr = isHrRole(user);
  const userEmail = user?.email || '';
  /*
   * Who to record against a decision, which is not the same question as whose records to read.
   *
   * userEmail stays the read key on purpose — it is matched with sameIdentity against stored
   * employeeEmail throughout this page, and feeding it a Cognito sub would stop those lookups
   * resolving. But an approver whose email attribute is unset made userEmail '', and that
   * empty string was written to the audit trail as the decider: HR Operations > Audit History
   * showed "Comp-Off Approved … Unknown", with no way to tell who had approved the days.
   * resolveActorId falls back to the sub, which actorFor over there now resolves to a name.
   */
  const actorId = resolveActorId(user);
  /*
   * Whether this session can be tied to an employee at all.
   *
   * An account with no email on it (Cognito attribute unset, or a federated identity whose
   * native twin could not be resolved) has no key for any of the per-employee reads on this
   * page. The services now return nothing for that rather than everything — which is the safe
   * answer but an indistinguishable one, since "0 days" reads exactly like a real empty
   * balance. Saying so is the point: the previous silent version showed the super admin a
   * balance of 1 built from another employee's approved day, and nothing on screen suggested
   * the number was not theirs.
   *
   * The approver and HR tabs stay fully usable — they read the table unscoped and never needed
   * the viewer's own identity to list other people's claims.
   */
  const identityKnown = isResolvedIdentity(userEmail);

  const [tab, setTab] = useState<Tab>('MINE');
  const [year, setYear] = useState(new Date().getFullYear());
  const [records, setRecords] = useState<CompOffRecord[]>([]);
  const [employees, setEmployees] = useState<SimpleUser[]>([]);
  const [reports, setReports] = useState<SimpleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [serverReachable, setServerReachable] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Apply form (the employee's own claim)
  const [workedDate, setWorkedDate] = useState(today());
  const [daysInput, setDaysInput] = useState('1');
  const [isHoliday, setIsHoliday] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // HR direct-grant form
  const [grantEmail, setGrantEmail] = useState('');
  const [grantSearch, setGrantSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [grantDate, setGrantDate] = useState(today());
  const [grantDays, setGrantDays] = useState('1');
  const [grantReason, setGrantReason] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);

  const [validityDraft, setValidityDraft] = useState(String(CompOffService.getValidityDays()));
  const validityDays = CompOffService.getValidityDays();

  // Stable so the loaders below can depend on it without re-running every render.
  const showToast = useCallback((msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3600);
  }, []);

  /** Read the local cache. Cheap and synchronous — used for same-tab event echoes. */
  const refresh = useCallback(() => {
    setRecords(CompOffService.getRecords());
    setValidityDraft(String(CompOffService.getValidityDays()));
  }, []);

  /**
   * Pull from the server, then re-read.
   *
   * Separate from `refresh` because the two have different costs: this one is for mount and
   * for after a decision, while `refresh` answers the storage/balance events that fire
   * several times per write and must not each trigger a network call.
   */
  const reload = useCallback(async () => {
    const ok = await CompOffService.refresh();
    refresh();
    // Distinguishing these matters most for an approver: an empty queue because nothing is
    // pending reads identically to an empty queue because the fetch failed.
    if (!ok) setServerReachable(false);
    else setServerReachable(true);
  }, [refresh]);

  useEffect(() => {
    refresh();
    setLoading(true);
    Promise.all([
      reload(),
      loadAllUsers()
        .then(setEmployees)
        .catch(() => setEmployees([])),
      (canDecide && !isHr ? loadDirectReports(userEmail) : Promise.resolve([]))
        .then(setReports)
        .catch(() => setReports([])),
    ]).finally(() => setLoading(false));
  }, [refresh, reload, canDecide, isHr, userEmail]);

  useEffect(() => {
    const evts = ['leave-balance-updated', 'leave-policy-updated', 'storage'];
    evts.forEach(e => window.addEventListener(e, refresh));
    return () => { evts.forEach(e => window.removeEventListener(e, refresh)); };
  }, [refresh]);

  const nameFor = useCallback((email: string) => {
    const m = employees.find(e => sameIdentity(e.email, email));
    return m ? (m.fullName || m.name) : email;
  }, [employees]);

  const inYear = useCallback(
    (r: CompOffRecord) => new Date(r.workedDate || r.createdAt).getFullYear() === year,
    [year]
  );

  /**
   * The team's Comp-Off for the selected year — the Team History tab.
   *
   * Scoped by `reports`, the same direct-reports list that bounds the Requests tab, so a manager can
   * never see an employee outside their reporting line. Empty reports means empty, never "therefore
   * everyone".
   */
  const teamHistory = useMemo(
    () => records
      .filter(r => reports.some(rep => sameIdentity(rep.email, r.employeeEmail)))
      .filter(inYear)
      .sort((a, b) => (b.workedDate || '').localeCompare(a.workedDate || '')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, reports, year]
  );

  /**
   * What a record's days are worth right now, in words.
   *
   * Says when a withheld day becomes spendable, so an approval a manager just made does not look
   * like it failed to register — see CompOffService.isApprovedToday.
   */
  const availabilityFor = useCallback((r: CompOffRecord): string => {
    if (r.status !== 'APPROVED') return '—';
    if (CompOffService.isApprovedToday(r)) return 'Usable from tomorrow';
    if ((r.workedDate || '') > CompOffService.todayIso()) return 'After the day is worked';
    const expiry = CompOffService.expiryDateFor(r.workedDate);
    return expiry ? `Usable until ${formatExpiryDate(expiry)}` : 'Usable';
  }, []);

  const myRecords = useMemo(
    () => records.filter(r => sameIdentity(r.employeeEmail, userEmail) && inYear(r))
      .sort((a, b) => (b.workedDate || '').localeCompare(a.workedDate || '')),
    [records, userEmail, inYear]
  );

  /**
   * The usable balance — the same set the leave balance counts, not merely "approved".
   *
   * This summed every APPROVED record for the year, which is a different number: it
   * included days that had already lapsed, and days granted for a shift not yet worked.
   * So this card could read "2 DAYS AVAILABLE" under a heading promising approved days
   * only, while the Leave Dashboard, Reports and the balance drawer all said 1 — and the
   * one the employee could actually spend was 1.
   *
   * usableRecords in CompOffService is the single definition (approved, in-year, worked,
   * unexpired). Deriving from it here rather than re-filtering keeps this page from
   * drifting away from the balance again the next time a rule changes.
   */
  const myEarned = useMemo(
    () => round1(CompOffService.getUsableDays(userEmail, year)),
    // records is a dependency because getUsableDays reads the same cache `records` came
    // from — without it this figure would not refresh after a claim is decided.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userEmail, year, records]
  );
  const myPending = useMemo(
    () => round1(myRecords.filter(r => r.status === 'PENDING').reduce((a, r) => a + r.daysGranted, 0)),
    [myRecords]
  );

  /**
   * Days approved today, which the balance is deliberately withholding until tomorrow.
   *
   * The rule is that a Comp-Off approved on the 21st cannot be spent on the 21st. It is correct,
   * and it was invisible: an employee who watched their manager approve a day saw the balance stay
   * at 0 with the record right underneath marked APPROVED. That is indistinguishable from the
   * approval having failed, and it is the first thing anybody asks about.
   *
   * CompOffService has exposed this list since the day the rule was added, for exactly this — it
   * had simply never been read by a screen.
   */
  const withheldToday = useMemo(
    // Same dependency reasoning as myEarned: the helper reads the cache `records` came from, so
    // without it this would not refresh when a claim is decided.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => CompOffService.getPendingUntilTomorrow(userEmail, year),
    [userEmail, year, records]
  );
  const withheldDays = useMemo(
    () => round1(withheldToday.reduce((a, r) => a + r.daysGranted, 0)),
    [withheldToday]
  );

  /**
   * Every date on which some of these days stop being usable, soonest first — not just the
   * next one.
   *
   * The expiry window runs from the date worked, so two claims worked a fortnight apart carry
   * two different deadlines. This used to keep `usable[0]` and discard the rest, which left
   * the card stating one date and "1 of your 2 day(s) lapse after that" — telling the employee
   * a second deadline exists while never saying when it falls, and giving them no way to plan
   * around it. One entry per distinct date fixes that.
   *
   * Built from the usable set, for the same reason as myEarned above: filtering on APPROVED
   * alone let an already-lapsed record win the sort, so the card could announce a deadline
   * that had passed while a genuine upcoming one sat further down the list, unmentioned.
   */
  const expirySchedule = useMemo(() => {
    const byDate = new Map<string, number>();
    CompOffService.getExpiringDays(userEmail, year).forEach(e => {
      byDate.set(e.expiresOn, (byDate.get(e.expiresOn) || 0) + e.days);
    });
    // Sorted explicitly rather than leaning on getExpiringDays' order — Map preserves
    // insertion order, which is only the same thing for as long as that function keeps
    // sorting. The dates are ISO, so a plain string compare is chronological.
    return Array.from(byDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, days]) => ({ date, days: round1(days) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, year, records]);

  /** The soonest deadline — what the progress bar counts down to. */
  const nextExpiry = expirySchedule.length > 0 ? expirySchedule[0] : null;

  /** Claims routed to this approver — HR sees all, a manager only their reports'. */
  const pending = useMemo(() => {
    const all = records.filter(r => r.status === 'PENDING' && inYear(r));
    if (isHr) return all;
    return all.filter(r => reports.some(rep => sameIdentity(rep.email, r.employeeEmail)));
  }, [records, reports, isHr, inYear]);

  const allRecords = useMemo(
    () => records.filter(inYear).sort((a, b) => (b.workedDate || '').localeCompare(a.workedDate || '')),
    [records, inYear]
  );

  const employeeMatches = useMemo(() => {
    const q = grantSearch.trim().toLowerCase();
    const pool = q
      ? employees.filter(e => (e.fullName || e.name).toLowerCase().includes(q) || e.email.toLowerCase().includes(q))
      : employees;
    return pool.slice(0, 20);
  }, [employees, grantSearch]);

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    // Refused rather than filed against an empty key: a row with no employeeEmail belongs to
    // nobody, would never appear in this employee's history, and no approver could route it.
    if (!identityKnown) {
      showToast('Your account has no email address, so a claim cannot be filed against it. Contact HR.', 'error');
      return;
    }
    const days = Number(daysInput);
    if (!Number.isFinite(days) || days <= 0) { showToast('Days must be greater than 0.', 'error'); return; }
    if (!reason.trim()) { showToast('Tell your manager why you worked that day.', 'error'); return; }
    if (workedDate > today()) { showToast('You can only claim a day you have already worked.', 'error'); return; }

    /*
     * No cap check. The configured number is how long a Comp-Off day stays usable, not a
     * maximum balance, so refusing a claim once the balance passed it was enforcing a rule that
     * does not exist — someone who legitimately worked more weekends than the window is long
     * would have had valid claims rejected. Days lapse on their expiry instead.
     */
    setSubmitting(true);
    try {
      await CompOffService.applyForCompOff(userEmail, workedDate, isHoliday, days, reason.trim());
      showToast('Comp-Off request sent to your manager.', 'success');
      setWorkedDate(today()); setDaysInput('1'); setReason(''); setIsHoliday(true);
      await reload();
    } catch {
      showToast('Could not send the request. Please retry.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (r: CompOffRecord, approve: boolean) => {
    let note: string | undefined;
    if (!approve) {
      const entered = window.prompt(`Why are you rejecting ${nameFor(r.employeeEmail)}'s Comp-Off claim?`);
      if (entered === null) return;
      if (!entered.trim()) { showToast('A reason is required to reject.', 'error'); return; }
      note = entered.trim();
    }
    setBusyId(r.id);
    try {
      const res = await CompOffService.decideCompOff(r.id, approve, actorId, note);
      if (!res.ok) { showToast(res.error || 'Could not record the decision.', 'error'); return; }
      showToast(
        approve
          ? `Approved — ${r.daysGranted} day(s) added to ${nameFor(r.employeeEmail)}'s Comp-Off.`
          : 'Request rejected.',
        'success'
      );
      await reload();
    } catch {
      showToast('Could not record the decision. Please retry.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    const days = Number(grantDays);
    if (!grantEmail) { showToast('Choose an employee.', 'error'); return; }
    if (!Number.isFinite(days) || days <= 0) { showToast('Days must be greater than 0.', 'error'); return; }
    if (!grantReason.trim()) { showToast('A reason is required.', 'error'); return; }

    // No cap check — see handleApply.
    setGranting(true);
    try {
      if (editingId) {
        await CompOffService.updateCompOff(
          editingId,
          { workedDate: grantDate, daysGranted: days, reason: grantReason.trim() },
          userEmail
        );
        showToast('Comp-Off updated.', 'success');
      } else {
        // Direct grant is credited immediately — HR is the approver. 'HR' is the capacity
        // recorded against it: this form is the HR tab, reachable only via isHrRole, and a
        // manager's grant goes through the Grant Comp-Off page under 'Manager' instead.
        await CompOffService.grantCompOff(grantEmail, grantDate, true, days, actorId, grantReason.trim(), 'HR');
        showToast('Comp-Off credited.', 'success');
      }
      setEditingId(null); setGrantEmail(''); setGrantSearch(''); setGrantDays('1'); setGrantReason('');
      await reload();
    } catch {
      showToast('Could not save. Please retry.', 'error');
    } finally {
      setGranting(false);
    }
  };

  const handleRemove = async (r: CompOffRecord) => {
    if (!window.confirm(`Remove ${r.daysGranted} day(s) Comp-Off for ${nameFor(r.employeeEmail)}?`)) return;
    setBusyId(r.id);
    try {
      // Reports the server's answer rather than assuming success: there is no local hide
      // list for Comp-Off, so a row that failed to delete really is still there.
      const removed = await CompOffService.deleteCompOff(r.id, userEmail, 'Removed by HR');
      showToast(
        removed ? 'Comp-Off removed.' : 'Could not remove that Comp-Off. Please retry.',
        removed ? 'success' : 'error'
      );
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const saveValidity = () => {
    const next = Number(validityDraft);
    if (!Number.isFinite(next) || next <= 0) {
      showToast('Enter a positive number of days.', 'error');
      return;
    }
    LeavePolicyService.saveSettings({ compOffValidityDays: next });
    // Says what the number does. "Cap set to 45 days" described a maximum balance.
    showToast(`Comp-Off must now be used within ${next} days of the date worked.`, 'success');
    refresh();
  };

  const pill = (s: string) =>
    s === 'APPROVED' ? 'is-good' : s === 'PENDING' ? 'is-warn' : 'is-crit';

  if (!user) return null;

  return (
    <section className="leave-page">
      <LeavePageHeader title="Apply Comp-Off" actions={<YearSelect value={year} onChange={setYear} />} />

      {toast && <div className={`ems-toast ems-toast--${toast.type}`}>{toast.msg}</div>}

      {!serverReachable && !loading && (
        // Said plainly, because the failure mode is silent: without this an approver reads an
        // empty queue as "my team has claimed nothing" when the fetch simply did not land.
        <div className="ems-card co-offline-note">
          Showing locally cached Comp-Off only — the server could not be reached, so recent
          claims and decisions from other people may be missing.
          <button type="button" className="ems-btn ems-btn--secondary" onClick={() => reload()}>
            Retry
          </button>
        </div>
      )}

      <nav className="ems-tabs" role="tablist" aria-label="Comp-Off views">
        <button type="button" role="tab" aria-selected={tab === 'MINE'}
          className={`ems-tab ${tab === 'MINE' ? 'active' : ''}`} onClick={() => setTab('MINE')}>
          My Comp-Off
        </button>
        {canDecide && (
          <button type="button" role="tab" aria-selected={tab === 'REQUESTS'}
            className={`ems-tab ${tab === 'REQUESTS' ? 'active' : ''}`} onClick={() => setTab('REQUESTS')}>
            Requests
            {pending.length > 0 && <span className="ems-tab-count">{pending.length}</span>}
          </button>
        )}
        {canDecide && !isHr && (
          <button type="button" role="tab" aria-selected={tab === 'HISTORY'}
            className={`ems-tab ${tab === 'HISTORY' ? 'active' : ''}`} onClick={() => setTab('HISTORY')}>
            Team History
          </button>
        )}
        {isHr && (
          <button type="button" role="tab" aria-selected={tab === 'MANAGE'}
            className={`ems-tab ${tab === 'MANAGE' ? 'active' : ''}`} onClick={() => setTab('MANAGE')}>
            Manage
          </button>
        )}
      </nav>

      {/* ── My Comp-Off: apply + own history ── */}
      {tab === 'MINE' && (
        <>
          {!identityKnown && (
            <div className="ems-card co-offline-note">
              {/* Names the account. The fix is an edit to one specific Cognito user, and without
                  the identifier the person reading this cannot say which one — the display name
                  is not unique and is not what User Management is keyed on. */}
              This account has no email address on it, so we cannot tell which Comp-Off records
              are yours. The balance below is not a figure you can rely on — ask HR to set the
              email on your user account
              {actorId ? <> (account <code>{actorId}</code>)</> : null}.
            </div>
          )}

          <div className="ems-card">
            <div className="ems-card-head">
              <div>
                <h3>Your Comp-Off Balance</h3>
                <p className="ems-card-sub">
                  Approved days only. Comp-Off is applied for like any other leave, and an
                  approved leave request draws this down.
                </p>
              </div>
              {/* The number is a validity window, so it is stated as one. It was previously a
                  "Cap 45 days" badge over a bar filling toward 45, which described a maximum
                  balance — a rule the system does not have. */}
              <span className="ems-badge ems-badge--info">Use within {validityDays} days</span>
            </div>
            <div className="co-ledger">
              <div className="co-ledger-item">
                <span className="co-ledger-value">{round1(myEarned)}</span>
                <span className="co-ledger-label">Days available</span>
              </div>
              {/*
                Why the balance has not moved, said where the balance is.
                Shown only when there is something to explain, so it does not become standing noise
                on the days it has nothing to say.
              */}
              {withheldDays > 0 && (
                <p className="co-ledger-note">
                  <strong>{withheldDays} day{withheldDays === 1 ? '' : 's'} approved today</strong>
                  {' '}— Comp-Off cannot be spent on the day it is approved, so{' '}
                  {withheldDays === 1 ? 'it joins' : 'they join'} your balance tomorrow.
                </p>
              )}
              {/* Progress toward the soonest expiry — time elapsed, not balance accumulated.
                  There is nothing to fill up toward, so the bar tracks the thing that is
                  actually running out. */}
              {nextExpiry && (
                <>
                  <div className="co-ledger-bar">
                    <span
                      className="co-ledger-fill"
                      style={{ width: `${Math.max(0, Math.min(100, 100 - (daysUntil(nextExpiry.date) / Math.max(1, validityDays)) * 100))}%` }}
                    />
                  </div>
                  <span className="co-ledger-cap">
                    {daysLeftLabel(daysUntil(nextExpiry.date))}
                  </span>
                </>
              )}
              {myPending > 0 && <span className="ems-badge ems-badge--warn">{myPending} awaiting approval</span>}
            </div>
            {/* The question the balance alone cannot answer: by when must these days be used?
                Without it the employee has to know the year-end rule and work it out.

                Two shapes, because one deadline and several are genuinely different questions.
                A single date belongs in a sentence; several belong in a list, one row each.
                Writing several into the same sentence is what produced the unreadable
                "Take by <date> — 1 of your 2 day(s) lapse after that", which named one
                deadline and left the other for the employee to guess at. */}
            {myEarned > 0 && expirySchedule.length === 1 && (
              <p className={`co-expiry-note ${daysUntil(expirySchedule[0].date) <= 7 ? 'co-expiry-note--soon' : ''}`}>
                {expirySchedule[0].days === round1(myEarned)
                  ? `Take by ${fmtDate(expirySchedule[0].date)} — all ${round1(myEarned)} day(s) lapse after that.`
                  : `Take by ${fmtDate(expirySchedule[0].date)} — ${expirySchedule[0].days} of your ${round1(myEarned)} day(s) lapse after that.`}
                {` ${daysLeftLabel(daysUntil(expirySchedule[0].date))}.`}
              </p>
            )}
            {myEarned > 0 && expirySchedule.length > 1 && (
              <div className="co-expiry-list">
                <p className="co-expiry-list-head">
                  Your {round1(myEarned)} days lapse on different dates — each was earned on a
                  different day, and the window runs from the day worked.
                </p>
                <ul>
                  {expirySchedule.map(e => {
                    const left = daysUntil(e.date);
                    return (
                      <li key={e.date} className={`co-expiry-row ${left <= 7 ? 'co-expiry-row--soon' : ''}`}>
                        <span className="co-expiry-days">{e.days} {e.days === 1 ? 'day' : 'days'}</span>
                        <span className="co-expiry-sep">take by</span>
                        <span className="co-expiry-date">{fmtDate(e.date)}</span>
                        <span className="co-expiry-left">{daysLeftLabel(left)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <form onSubmit={handleApply} className="ems-card">
            <div className="ems-card-head">
              <div>
                <h3>Apply for Comp-Off</h3>
                <p className="ems-card-sub">
                  Claim a weekend or public holiday you worked. Your manager approves it before
                  the days are added to your balance.
                </p>
              </div>
            </div>
            <div className="ems-form-grid">
              <div className="ems-field">
                <label htmlFor="co-worked">Date worked</label>
                <input id="co-worked" type="date" max={today()} value={workedDate}
                  onChange={e => setWorkedDate(e.target.value)} required />
              </div>
              <div className="ems-field">
                <label htmlFor="co-days">Days</label>
                <input id="co-days" type="number" min={0.5} step={0.5} value={daysInput}
                  onChange={e => setDaysInput(e.target.value)} required />
              </div>
              <div className="ems-field ems-field--wide">
                <label htmlFor="co-why">Reason</label>
                <input id="co-why" type="text" placeholder="e.g. Production release support"
                  value={reason} onChange={e => setReason(e.target.value)} required />
              </div>
              <div className="ems-field ems-field--wide">
                <label className="ems-inline-check">
                  <input type="checkbox" checked={isHoliday} onChange={e => setIsHoliday(e.target.checked)} />
                  It was a company holiday (otherwise a weekend)
                </label>
              </div>
            </div>
            <div className="ems-form-actions">
              <button type="submit" className="ems-btn-primary" disabled={submitting || !identityKnown}>
                {submitting ? 'Sending…' : 'Submit request'}
              </button>
            </div>
          </form>

          <div className="ems-card ems-card--table">
            <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
              <div>
                <h3>Your Requests</h3>
                <p className="ems-card-sub">{myRecords.length} record(s) in {year}</p>
              </div>
            </div>
            {loading ? <p className="ems-empty">Loading…</p> : myRecords.length === 0 ? (
              <EmptyState icon="inbox" title="No Comp-Off yet"
                subtitle={`You have not claimed any Comp-Off for ${year}.`} />
            ) : (
              <div className="ems-table-scroll">
                <table className="ems-table">
                  <thead><tr><th>Date worked</th><th>Type</th><th>Days</th><th>Use by</th><th>Reason</th><th>Status</th><th>Approved by</th></tr></thead>
                  <tbody>
                    {myRecords.map(r => (
                      <tr key={r.id}>
                        {/* Both dates spelled out, not just the expiry one. Formatting only the
                            expiry would leave "2026-08-08" sitting next to "22 Sep 2026" in the
                            same row — one fact in two notations reads as two kinds of date. */}
                        <td>{fmtDate(r.workedDate)}</td>
                        <td>{r.isHoliday ? 'Holiday' : 'Weekend'}</td>
                        <td><strong>{r.daysGranted}</strong></td>
                        {/* Only meaningful once approved — a pending or rejected claim has no
                            days to use, so a deadline against it would be noise. */}
                        <td>{r.status === 'APPROVED' ? fmtDate(expiryOf(r)) : '—'}</td>
                        <td>{r.reason || '—'}</td>
                        <td><span className={`lr-pill ${pill(r.status)}`}>{r.status}</span></td>
                        {/* The approver's email, not their display name: the employee needs
                            to know who to contact about the decision, and the directory
                            lookup behind nameFor is unavailable to most employees anyway,
                            so it silently fell back to the email for them regardless. */}
                        <td>{r.decidedBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Approver queue ── */}
      {tab === 'REQUESTS' && canDecide && (
        <div className="ems-card ems-card--table">
          <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
            <div>
              <h3>Comp-Off Requests</h3>
              <p className="ems-card-sub">Approving adds the days to the employee's balance.</p>
            </div>
          </div>
          {loading ? <p className="ems-empty">Loading…</p> : pending.length === 0 ? (
            <EmptyState icon="inbox" title="Nothing awaiting you"
              subtitle="Comp-Off claims from your team will appear here." />
          ) : (
            <div className="ems-table-scroll">
              <table className="ems-table">
                <thead><tr><th>Employee</th><th>Date worked</th><th>Type</th><th>Days</th><th>Reason</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {pending.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div className="la-emp-info">
                          <strong>{nameFor(r.employeeEmail)}</strong>
                          <span className="la-emp-email">{r.employeeEmail}</span>
                        </div>
                      </td>
                      <td>{r.workedDate}</td>
                      <td>{r.isHoliday ? 'Holiday' : 'Weekend'}</td>
                      <td><strong>{r.daysGranted}</strong></td>
                      <td>{r.reason || '—'}</td>
                      <td>
                        <div className="ems-actionbar" style={{ margin: 0 }}>
                          <button type="button" className="ems-btn ems-btn--success"
                            disabled={busyId === r.id} onClick={() => handleDecision(r, true)}>
                            {busyId === r.id ? '…' : 'Approve'}
                          </button>
                          <button type="button" className="ems-btn ems-btn--danger"
                            disabled={busyId === r.id} onClick={() => handleDecision(r, false)}>
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Manager: read-only history for the team ── */}
      {tab === 'HISTORY' && canDecide && !isHr && (
        <div className="ems-card ems-card--table">
          <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
            <div>
              <h3>Team Comp-Off History</h3>
              <p className="ems-card-sub">
                Every Comp-Off day for the {reports.length}{' '}
                {reports.length === 1 ? 'employee who reports' : 'employees who report'} to you with
                a {year} worked date — claimed or granted, and where each one stands.
              </p>
            </div>
          </div>

          {reports.length === 0 ? (
            <EmptyState
              icon="inbox"
              title="Nobody reports to you"
              subtitle="Comp-Off history covers employees whose manager is set to you. Ask HR to check the reporting line in User Management."
            />
          ) : teamHistory.length === 0 ? (
            <EmptyState
              icon="inbox"
              title={`No Comp-Off in ${year}`}
              subtitle="Days your team claims or is granted will appear here."
            />
          ) : (
            <div className="ems-table-scroll">
              <table className="ems-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date Worked</th>
                    <th>Days</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Decided By</th>
                    <th>Decided On</th>
                    <th>Availability</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {teamHistory.map(r => (
                    <tr key={r.id}>
                      <td>{nameFor(r.employeeEmail)}</td>
                      <td className="la-dates">{r.workedDate || '—'}</td>
                      <td>{round1(r.daysGranted)}</td>
                      <td>{r.isHoliday ? 'Holiday' : 'Weekend / Extra'}</td>
                      <td>
                        <span
                          className={`lr-pill ${
                            r.status === 'APPROVED' ? 'is-good'
                              : r.status === 'PENDING' ? 'is-warn'
                              : ''
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td>{r.decidedBy ? nameFor(r.decidedBy) : '—'}</td>
                      <td className="la-dates">{CompOffService.localDateOf(r.decidedAt) || '—'}</td>
                      <td>{availabilityFor(r)}</td>
                      <td>{r.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── HR: direct grant, corrections, cap ── */}
      {tab === 'MANAGE' && isHr && (
        <>
          <form onSubmit={handleGrant} className="ems-card">
            <div className="ems-card-head">
              <div>
                <h3>{editingId ? 'Edit Comp-Off' : 'Credit Comp-Off directly'}</h3>
                <p className="ems-card-sub">
                  For corrections, or to credit someone who never filed a claim. Applied
                  immediately — you are the approver.
                </p>
              </div>
            </div>
            <div className="ems-form-grid">
              <div className="ems-field ems-field--wide lb-employee-search-field">
                <label htmlFor="co-grant-emp">Employee</label>
                <input id="co-grant-emp" type="text" autoComplete="off" placeholder="Search by name or email"
                  value={grantSearch} disabled={!!editingId}
                  onChange={e => { setGrantSearch(e.target.value); setGrantEmail(''); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)} required />
                {showDropdown && !editingId && (
                  <div className="lb-employee-search-dropdown">
                    {employeeMatches.length === 0 ? (
                      <div className="lb-employee-search-empty">No employees match your search.</div>
                    ) : employeeMatches.map(e => (
                      <button type="button" key={e.email} className="lb-employee-search-item"
                        onMouseDown={evt => evt.preventDefault()}
                        onClick={() => { setGrantEmail(e.email); setGrantSearch(`${e.fullName || e.name} (${e.email})`); setShowDropdown(false); }}>
                        <span>{e.fullName || e.name}</span>
                        <span className="lb-employee-search-item-email">{e.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="ems-field">
                <label htmlFor="co-grant-date">Date worked</label>
                <input id="co-grant-date" type="date" value={grantDate} onChange={e => setGrantDate(e.target.value)} required />
              </div>
              <div className="ems-field">
                <label htmlFor="co-grant-days">Days</label>
                <input id="co-grant-days" type="number" min={0.5} step={0.5} value={grantDays}
                  onChange={e => setGrantDays(e.target.value)} required />
              </div>
              <div className="ems-field ems-field--wide">
                <label htmlFor="co-grant-reason">Reason</label>
                <input id="co-grant-reason" type="text" value={grantReason} onChange={e => setGrantReason(e.target.value)} required />
              </div>
            </div>
            <div className="ems-form-actions">
              <button type="submit" className="ems-btn-primary" disabled={granting}>
                {granting ? 'Saving…' : editingId ? 'Save changes' : 'Credit Comp-Off'}
              </button>
              {editingId && (
                <button type="button" className="ems-btn ems-btn--secondary"
                  onClick={() => { setEditingId(null); setGrantEmail(''); setGrantSearch(''); setGrantDays('1'); setGrantReason(''); }}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="ems-card">
            <div className="ems-card-head">
              <div>
                <h3>Utilization of Comp-Off</h3>
                <p className="ems-card-sub">
                  How long an approved Comp-Off day stays usable, counted from the date worked.
                  After that it lapses and stops counting toward the employee's balance.
                </p>
              </div>
            </div>
            <div className="ems-form-grid">
              <div className="ems-field">
                <label htmlFor="co-validity">Days to use it within</label>
                <input id="co-validity" type="number" min={1} step={1} value={validityDraft} onChange={e => setValidityDraft(e.target.value)} />
              </div>
            </div>
            <div className="ems-form-actions">
              <button type="button" className="ems-btn-primary" onClick={saveValidity}>Save limit</button>
            </div>
          </div>

          <div className="ems-card ems-card--table">
            <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
              <div>
                <h3>All Comp-Off</h3>
                <p className="ems-card-sub">{allRecords.length} record(s) in {year}</p>
              </div>
            </div>
            {allRecords.length === 0 ? (
              <EmptyState icon="inbox" title="No Comp-Off recorded" subtitle={`Nothing for ${year} yet.`} />
            ) : (
              <div className="ems-table-scroll">
                <table className="ems-table">
                  <thead><tr><th>Employee</th><th>Date worked</th><th>Days</th><th>Status</th><th>Reason</th><th aria-label="Actions" /></tr></thead>
                  <tbody>
                    {allRecords.map(r => (
                      <tr key={r.id}>
                        <td>{nameFor(r.employeeEmail)}</td>
                        <td>{r.workedDate}</td>
                        <td><strong>{r.daysGranted}</strong></td>
                        <td><span className={`lr-pill ${pill(r.status)}`}>{r.status}</span></td>
                        <td>{r.reason || '—'}</td>
                        <td>
                          <div className="ems-actionbar" style={{ margin: 0 }}>
                            <button type="button" className="ems-btn ems-btn--secondary"
                              onClick={() => {
                                setEditingId(r.id); setGrantEmail(r.employeeEmail);
                                setGrantSearch(`${nameFor(r.employeeEmail)} (${r.employeeEmail})`);
                                setGrantDate(r.workedDate); setGrantDays(String(r.daysGranted)); setGrantReason(r.reason || '');
                              }}>
                              Edit
                            </button>
                            <button type="button" className="ems-btn ems-btn--danger"
                              disabled={busyId === r.id} onClick={() => handleRemove(r)}>
                              {busyId === r.id ? '…' : 'Remove'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ApplyCompOffPage;
